import React, { PropTypes, PureComponent } from 'react';
import { View, StyleSheet } from 'react-native';
import { createContainer, defer } from '@dbk-legacy/muster/react';
import { ref } from '@dbk-legacy/muster/utils';
import Error from '../../../../../../src/components/common/error';
import { LoadingCircle } from '../../../../../../src/components/common/loading';
import { Large, Small } from '../../../../../../src/components/common/text';
import AbbreviatedNotes from '../../../../../../src/components/common/abbreviated-notes';
import PositionHeader from './position-header/position-header-container';
import Transaction from './transaction/transaction-container';
import TransactionDetails from './transaction-details/transaction-details-container';
import ScrollableLayout from '../../../../../../src/components/common/scrollable-layout';
import SortingHeader from '../../../../../../src/components/common/sorting-header/sorting-header-container';
import { StickyContainer, Sticky } from 'react-sticky';
import Pagination from '../../../../../../src/components/common/pagination';
import styles from './position-transactions-factory.styles';
import AssetAccordion from '../../../../../../src/components/common/asset-accordion/asset-accordion.container';
import Search from '@dbk/ui-search';
import { Close } from '@dbk/ui-icons';

const MAX_TRANSACTIONS_DISPLAY_LIMIT = 250

export default function transactionsFactory({
  assetCategory,
  HeaderComponent = PositionHeader,
  SortingHeaderComponent = SortingHeader,
  RowComponent = Transaction,
  ExpandedRowComponent = TransactionDetails,
}) {
  class Transactions extends PureComponent {
    state = {
      searchValue: '',
    };

    handleSearchChange = (value) => {
      this.setState({ searchValue: value });
    }

    render () {
      const { ui, portfolio, screen, i18n: {messages} } = this.props

      ui.changeStateTitle({custometitle:messages['app.common.pageTitle.transactions']});
      const uiState = ui.state.position;
      const uiStatic = ui.static[assetCategory].position;
      const positionData = ui.positions[assetCategory].currentPosition;
      const { searchValue } = this.state;
      const transactions = Array.isArray(positionData.transactions) ? positionData.transactions : [];
      const rows = transactions.filter(row => row);
      const filteredRows = searchValue
        ? rows.filter(row =>
            row && row.type && row.type.toLowerCase().includes(searchValue.toLowerCase()) ||
            (row.transactionNo && row.transactionNo.toString().toLowerCase().includes(searchValue.toLowerCase())) ||
            (row.tranTypeDesc && row.tranTypeDesc.toLowerCase().includes(searchValue.toLowerCase())) ||
            (row.securityLongName && row.securityLongName.toLowerCase().includes(searchValue.toLowerCase()))
          )
        : rows;
      const displayRows = filteredRows;

      return (
        <div className={'position-transactions row'}>
          <StickyContainer style={{minHeight: screen.height/2.4 ,marginTop: '5px'}}>
            <HeaderComponent uiStatic={uiStatic} positionData={positionData} currency={positionData.currency} portfolio={portfolio} messages={messages} langCode={this.props.auth.langCode}/>
            <Sticky fixedTop={90}>
              {({ isSticky, wasSticky, distanceFromTop, distanceFromBottom, calculatedHeight }) =>
                <div style={{ ...this.props.style, overflow: 'auto', justifyContent: 'space-between', marginTop: 10}}>
                  <div style={{display: 'flex', alignItems: 'center', padding: '10px'}}>
                    <Search
                      size="large"
                      iconRight={Close}
                      onChange={this.handleSearchChange}
                      textID="input-searchId"
                      value={this.state.searchValue}
                      keyboardShouldPersistTaps='always'
                      underlineColorAndroid="transparent"
                      accessible={true}
                      placeholder={messages['app.position.search.placeholder']}
                      accessibilityLabel={messages['app.position.search.placeholder']}
                      style={StyleSheet.flatten(styles.searchWidth)}
                    />
                    <div style={StyleSheet.flatten(styles.infoMessage)}>
                      {positionData.transactionCount >= MAX_TRANSACTIONS_DISPLAY_LIMIT && (
                        <Small>{messages['app.position.maxTransactions']}</Small>
                      )}
                    </div>
                  </div>
                  <SortingHeader
                    uiStatic={uiStatic}
                    state={uiState}
                    currency={positionData.currency}
                    screen={screen}
                    messages={messages}
                    isPosTransFactory
                  />
                </div>
              }
            </Sticky>
            <ScrollableLayout
              style={{ paddingBottom: 20 }}
              testID={`${assetCategory}-view`}
              ref={r => this.rootView = r}
              body={rows.length === 0 ?
                <View style={styles.root} accessible={true} accessibilityLabel={messages['app.position.noTransDetails']}>
                  <Large type="secondary" style={styles.emptyText}>{messages['app.position.noTransDetails']}</Large>
                </View> :
                <AssetAccordion
                  isPosTransFactory
                  uiStatic={uiStatic}
                  state={uiState}
                  messages={messages}
                  rows={displayRows}
                  renderRow={row => <RowComponent
                    row={row}
                    uiStatic={uiStatic}
                    messages={messages}
                    assetCategory={assetCategory}
                    screen={screen}
                    abbreviationRules={ui.static.abbreviationRules}
                    langCode={this.props.auth.langCode}
                    searchValue={searchValue}
                  />}
                  renderRowExpanded={row => <ExpandedRowComponent
                    uiStatic={uiStatic}
                    row={row}
                    assetCategory={assetCategory}
                    abbreviationRules={ui.static.abbreviationRules}
                    messages={messages}
                  />}
                  footer={<View>
                    <AbbreviatedNotes style={{ paddingTop: 10 }}/>
                  </View>}
                />} />
          </StickyContainer>
        </div>
      );
    }
  }
  return createContainer({
    $renderError: () => <Error />,
    $renderLoading: () => <LoadingCircle isPage/>,
    $queries: {
      settings: {
        currentEnvironment: {
          id: true,
        },
      },
      screen:{
        height: true,
        width: true,
        isSmall: true,
      },
      i18n: {
        messages: true,
      },
      ui: {
        changeStateTitle: { $call: 'change.customtitle' },
        static: {
          [assetCategory]: {
            position: {
              ...AssetAccordion.getFragment('uiStatic'),
              ...RowComponent.getFragment('uiStatic'),
              ...SortingHeader.getFragment('uiStatic'),
              ...HeaderComponent.getFragment('uiStatic'),
              ...ExpandedRowComponent.getFragment('uiStatic'),
            },
          },
          abbreviationRules: true,
        },

        state: {
          position: {
            ...AssetAccordion.getFragment('state'),
            ...SortingHeader.getFragment('state'),
          },
        },

        positions: {
          [assetCategory]: {
            currentPosition: {
              ...HeaderComponent.getFragment('positionData'),
              currency: true,
              transactions: {
                $type: 'list',
                $range: {
                  offset: 0,
                  length: ref(`ui.positions.${assetCategory}.currentPosition.transactionCount`),
                },
                $node: defer({
                  type: true, // TODO: remove when DGUI-948 is fixed
                  transactionNo: true,
                  tranTypeDesc: true,
                  securityLongName: true,
                  ...RowComponent.getFragment('row'),
                  ...ExpandedRowComponent.getFragment('row'),
                }),
              },
              transactionCount: true,
              transactionPageSize: true
            },
          },
        }
      },
      portfolio: {
        ...HeaderComponent.getFragment('portfolio'),
        assetClassName:true,
      },
    },
  })(Transactions);
}
